import {
  AuthActions,
  PanelContainer,
  Selectors,
  useIsDesktop,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {RewardsInfoState} from '@eCommerce/frontend-legacy-shared-library-native/lib/typescript/store/auth';
import {Text, View} from 'native-base';
import React, {useCallback, useEffect, useState} from 'react';

import GenericAPIErrorDisplay from 'components/Offers/GenericAPIErrorDisplay';

import CreditsDisplay from './CreditsDisplay';
import MonthDropdown from './MonthDropdown';
import {MonthObj, getMonths} from './MonthDropdown/months';

const RewardsHistory = () => {
  const months = getMonths();
  const isDesktop = useIsDesktop();
  const dispatch = useWidgetDispatch();
  const [selectedMonth, setSelectedMonth] = useState<MonthObj | undefined>(
    months[0]
  );

  const rewardsInfo = useWidgetState(
    Selectors.rewardsInfoSelector
  ) as RewardsInfoState;

  const apiError =
    rewardsInfo?.rewardsInfoStatus === 'service_fail' ||
    rewardsInfo?.rewardsInfoStatus === 'http_error';

  const [errorMessageShown, setErrorMessageShown] = useState(apiError);

  const generateDatePayload = (monthData: MonthObj) => {
    const [, year] = monthData.name.split(' ');
    const adjustedKey = (parseInt(monthData.key, 10) - 1).toString();
    const startDate = getMonthFirstDay(year, adjustedKey);
    const endDate = getMonthLastDay(year, adjustedKey);

    return {startDate, endDate};
  };

  const retryHistoryAPI = useCallback(() => {
    if (!selectedMonth?.name) {
      return;
    }
    const {startDate, endDate} = generateDatePayload(selectedMonth);
    dispatch(AuthActions.getRewardCredits({startDate, endDate}));
  }, [dispatch, selectedMonth?.name]);

  useEffect(() => {
    // error message goes away only when we successfully hit api
    if (
      rewardsInfo?.rewardsInfoStatus === 'service_success' ||
      rewardsInfo?.rewardsInfoStatus === 'empty_information'
    ) {
      setErrorMessageShown(false);
    }
  }, [rewardsInfo?.rewardsInfoStatus]);

  const getMonthFirstDay = (year: string, month: string) => {
    const firstDay = new Date(parseInt(year, 10), parseInt(month, 10), 1);
    return `${
      firstDay.getMonth() + 1
    }/${firstDay.getDate()}/${firstDay.getFullYear()}`;
  };

  const getMonthLastDay = (year: string, month: string) => {
    const lastDay = new Date(parseInt(year, 10), parseInt(month, 10) + 1, 0);
    return `${
      lastDay.getMonth() + 1
    }/${lastDay.getDate()}/${lastDay.getFullYear()}`;
  };

  const handleSelectMonth = useCallback(
    ({key, name}, force = false) => {
      setSelectedMonth(prevState => {
        if (prevState?.name !== name || force) {
          const {startDate, endDate} = generateDatePayload({key, name});
          dispatch(AuthActions.getRewardCredits({startDate, endDate}));
          return {key, name};
        }

        return prevState;
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (selectedMonth?.name) {
      const {key, name} = selectedMonth;
      handleSelectMonth({key, name}, true);
    }
  }, []);

  if (
    !errorMessageShown &&
    (rewardsInfo?.rewardsInfoStatus === 'getting_info' ||
      rewardsInfo?.rewardsInfoStatus === 'initial')
  ) {
    return (
      <PanelContainer
        defaultOpen={false}
        icon="history"
        panelTitle="REWARDS HISTORY">
        <Text
          mb="32px"
          fontSize={isDesktop ? 4 : 3}
          lineHeight={isDesktop ? '26px' : '24px'}>
          Loading...
        </Text>
      </PanelContainer>
    );
  }

  if (!errorMessageShown) {
    return (
      <PanelContainer
        defaultOpen={false}
        icon="history"
        panelTitle="REWARDS HISTORY">
        <View marginBottom="32px">
          <View
            mb={selectedMonth ? '32px' : undefined}
            maxW={isDesktop ? '190px' : '300px'}>
            <MonthDropdown
              onSelect={handleSelectMonth}
              selected={selectedMonth}
              months={months}
            />
          </View>

          <View>
            <CreditsDisplay selectedMonth={selectedMonth} />
          </View>
        </View>
        <Text
          fontSize={isDesktop ? 4 : 3}
          lineHeight={isDesktop ? '26px' : '24px'}>
          eStatements are posted on or about the 7th day of each month.{' '}
          <Text
            bold
            fontSize={isDesktop ? 4 : 3}
            lineHeight={isDesktop ? '26px' : '24px'}>
            Please note:
          </Text>{' '}
          some eStatements are compiled in advance and will not reflect recent
          play.
        </Text>
      </PanelContainer>
    );
  } else {
    return (
      <View marginTop={!isDesktop ? '32px' : ''}>
        <GenericAPIErrorDisplay
          onRetryPress={apiError ? retryHistoryAPI : undefined}
        />
      </View>
    );
  }
};

export default RewardsHistory;
